import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import Logo from "@/components/Logo";
import {
    ArrowLeft, Loader2, Upload, X, CheckCircle2, Image as ImageIcon, Camera,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASONS = [
    "Fejl på produkt",
    "Fejlmål",
    "Forkert farve",
    "Forkert betjening",
];

const BRANDS = [
    { value: "gardinbussen", label: "Gardinbussen" },
    { value: "gardinfabrikken", label: "Gardinfabrikken" },
];

export default function MontorReport() {
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const cameraInputRef = useRef(null);

    const [montorName, setMontorName] = useState("");
    const [customerName, setCustomerName] = useState("");
    const [orderNumber, setOrderNumber] = useState("");
    const [reason, setReason] = useState("");
    const [description, setDescription] = useState("");
    const [brand, setBrand] = useState("gardinbussen");
    const [recipientKey, setRecipientKey] = useState(""); // "supplier:ID" or "employee:ID"
    const [files, setFiles] = useState([]); // {id, filename}
    const [uploading, setUploading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(null); // {case_number, recipient_name}

    const [recipients, setRecipients] = useState({ suppliers: [], employees: [] });
    const [loadingRecipients, setLoadingRecipients] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API}/montor/recipients`);
                setRecipients(data);
            } catch (e) {
                toast.error("Kunne ikke hente modtagere");
            } finally {
                setLoadingRecipients(false);
            }
        })();
    }, []);

    const handleFileSelect = async (e) => {
        const selected = Array.from(e.target.files || []);
        if (!selected.length) return;
        setUploading(true);
        for (const file of selected) {
            try {
                const fd = new FormData();
                fd.append("file", file);
                const { data } = await axios.post(`${API}/public/upload`, fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                setFiles((prev) => [...prev, { id: data.id, filename: data.filename }]);
            } catch (err) {
                toast.error(err.response?.data?.detail || `Kunne ikke uploade ${file.name}`);
            }
        }
        setUploading(false);
        e.target.value = "";
    };

    const removeFile = (fid) => setFiles((prev) => prev.filter((f) => f.id !== fid));

    const submit = async (e) => {
        e.preventDefault();
        if (!montorName.trim()) return toast.error("Indtast dit navn");
        if (!customerName.trim()) return toast.error("Indtast kundens navn");
        if (!orderNumber.trim()) return toast.error("Indtast ordrenummer");
        if (!reason) return toast.error("Vælg en årsag");
        if (!description.trim()) return toast.error("Beskriv problemet");
        if (!recipientKey) return toast.error("Vælg modtager");

        const [recipient_type, recipient_id] = recipientKey.split(":");
        const payload = {
            montor_name: montorName.trim(),
            customer_name: customerName.trim(),
            order_number: orderNumber.trim(),
            reason,
            description: description.trim(),
            brand,
            recipient_type,
            file_ids: files.map((f) => f.id),
        };
        if (recipient_type === "supplier") payload.supplier_id = recipient_id;
        else payload.employee_id = recipient_id;

        setSubmitting(true);
        try {
            const { data } = await axios.post(`${API}/montor/complaint`, payload);
            setSuccess({ case_number: data.case_number, recipient_name: data.recipient_name });
        } catch (err) {
            toast.error(err.response?.data?.detail || "Kunne ikke indsende reklamation");
        } finally {
            setSubmitting(false);
        }
    };

    if (success) {
        return (
            <div className="min-h-screen bg-[#FAFAF9]" data-testid="montor-report-success">
                <div className="max-w-md mx-auto px-4 py-10">
                    <div className="flex justify-center mb-6">
                        <Logo size="md" to={null} />
                    </div>
                    <div className="bg-white border border-[#E5E1DA] rounded-xl p-6 text-center">
                        <div className="mx-auto w-12 h-12 rounded-full bg-[#F0EBE1] flex items-center justify-center mb-4">
                            <CheckCircle2 className="w-6 h-6 text-[#4A4641]" />
                        </div>
                        <h1 className="font-heading text-xl font-medium text-[#2C2A29] mb-2">Reklamation indmeldt</h1>
                        <p className="text-sm text-[#5C5854] mb-4">
                            Sagsnummer{" "}
                            <span className="font-mono font-semibold text-[#2C2A29]" data-testid="montor-report-case-number">
                                {success.case_number}
                            </span>
                            {" "}er oprettet og sendt til{" "}
                            <span className="font-medium text-[#2C2A29]">{success.recipient_name}</span>.
                        </p>
                        <p className="text-xs text-[#78736E] mb-6">
                            Kundeservice følger op på sagen og tager kontakt til kunden hvis det er nødvendigt.
                        </p>
                        <div className="flex flex-col gap-2">
                            <Button
                                onClick={() => {
                                    setSuccess(null);
                                    setMontorName((n) => n); // keep name for next report
                                    setCustomerName("");
                                    setOrderNumber("");
                                    setReason("");
                                    setDescription("");
                                    setRecipientKey("");
                                    setFiles([]);
                                }}
                                className="w-full bg-[#4A4641] hover:bg-[#2C2A29] text-white"
                                data-testid="montor-report-new-another"
                            >
                                Indmeld en til
                            </Button>
                            <Link
                                to="/montor"
                                className="text-sm text-[#78736E] hover:text-[#2C2A29] py-2"
                            >
                                Tilbage til opslag
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#FAFAF9]" data-testid="montor-report-page">
            <meta name="robots" content="noindex, nofollow" />
            <div className="max-w-md mx-auto px-4 py-6">
                <Link
                    to="/montor"
                    className="flex items-center gap-1.5 text-sm text-[#78736E] hover:text-[#2C2A29] mb-4"
                    data-testid="montor-report-back"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Tilbage til opslag
                </Link>

                <div className="flex justify-center mb-6">
                    <Logo size="md" to={null} />
                </div>

                <div className="bg-white border border-[#E5E1DA] rounded-xl p-6">
                    <h1 className="font-heading text-xl font-medium text-[#2C2A29] mb-1">Indmeld reklamation</h1>
                    <p className="text-sm text-[#78736E] mb-5">
                        Opdager du en fejl fra producenten eller på opmålingen, kan du melde den ind her direkte fra kunden.
                    </p>

                    <form onSubmit={submit} className="space-y-4">
                        <div>
                            <Label htmlFor="montor-name" className="text-xs font-semibold uppercase tracking-wider text-[#78736E]">Dit navn</Label>
                            <Input
                                id="montor-name"
                                value={montorName}
                                onChange={(e) => setMontorName(e.target.value)}
                                placeholder="fx Peter Hansen"
                                className="mt-1.5"
                                disabled={submitting}
                                autoFocus
                                data-testid="montor-report-montor-name"
                            />
                        </div>

                        <div>
                            <Label htmlFor="customer-name" className="text-xs font-semibold uppercase tracking-wider text-[#78736E]">Kundens navn</Label>
                            <Input
                                id="customer-name"
                                value={customerName}
                                onChange={(e) => setCustomerName(e.target.value)}
                                placeholder="fx Jens Jensen"
                                className="mt-1.5"
                                disabled={submitting}
                                data-testid="montor-report-customer-name"
                            />
                        </div>

                        <div>
                            <Label htmlFor="order-number" className="text-xs font-semibold uppercase tracking-wider text-[#78736E]">Ordrenummer</Label>
                            <Input
                                id="order-number"
                                value={orderNumber}
                                onChange={(e) => setOrderNumber(e.target.value)}
                                placeholder="fx 5017727"
                                className="mt-1.5 font-mono"
                                disabled={submitting}
                                data-testid="montor-report-order-number"
                            />
                        </div>

                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider text-[#78736E]">Brand</Label>
                            <Select value={brand} onValueChange={setBrand} disabled={submitting}>
                                <SelectTrigger className="mt-1.5" data-testid="montor-report-brand">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {BRANDS.map((b) => (
                                        <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider text-[#78736E]">Årsag</Label>
                            <Select value={reason} onValueChange={setReason} disabled={submitting}>
                                <SelectTrigger className="mt-1.5" data-testid="montor-report-reason">
                                    <SelectValue placeholder="Vælg årsag" />
                                </SelectTrigger>
                                <SelectContent>
                                    {REASONS.map((r) => (
                                        <SelectItem key={r} value={r} data-testid={`montor-report-reason-${r}`}>{r}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <Label htmlFor="description" className="text-xs font-semibold uppercase tracking-wider text-[#78736E]">Beskrivelse</Label>
                            <Textarea
                                id="description"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Beskriv kort hvad der er galt..."
                                rows={4}
                                className="mt-1.5"
                                disabled={submitting}
                                data-testid="montor-report-description"
                            />
                        </div>

                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider text-[#78736E]">Billeder <span className="normal-case text-[#A19E9A] font-normal">(valgfrit)</span></Label>
                            <div className="mt-1.5">
                                <input
                                    ref={cameraInputRef}
                                    type="file"
                                    accept="image/*"
                                    capture="environment"
                                    onChange={handleFileSelect}
                                    className="hidden"
                                    disabled={submitting || uploading}
                                    data-testid="montor-report-camera-input"
                                />
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*,.pdf,.heic,.heif"
                                    multiple
                                    onChange={handleFileSelect}
                                    className="hidden"
                                    disabled={submitting || uploading}
                                    data-testid="montor-report-file-input"
                                />
                                <div className="grid grid-cols-2 gap-2">
                                    <button
                                        type="button"
                                        onClick={() => cameraInputRef.current?.click()}
                                        disabled={submitting || uploading}
                                        className="flex items-center justify-center gap-2 border border-dashed border-[#D4CFC5] rounded-lg px-3 py-3 text-sm text-[#5C5854] hover:border-[#4A4641] hover:text-[#2C2A29] transition-colors disabled:opacity-50"
                                        data-testid="montor-report-camera-button"
                                    >
                                        {uploading ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <><Camera className="w-4 h-4" /> Tag billede</>
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={submitting || uploading}
                                        className="flex items-center justify-center gap-2 border border-dashed border-[#D4CFC5] rounded-lg px-3 py-3 text-sm text-[#5C5854] hover:border-[#4A4641] hover:text-[#2C2A29] transition-colors disabled:opacity-50"
                                        data-testid="montor-report-upload-button"
                                    >
                                        {uploading ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <><Upload className="w-4 h-4" /> Fra galleri</>
                                        )}
                                    </button>
                                </div>
                                {files.length > 0 && (
                                    <div className="mt-2 space-y-1.5" data-testid="montor-report-file-list">
                                        {files.map((f) => (
                                            <div key={f.id} className="flex items-center gap-2 bg-[#FAF9F7] border border-[#E5E1DA] rounded-md px-3 py-2 text-sm">
                                                <ImageIcon className="w-4 h-4 text-[#78736E] shrink-0" />
                                                <span className="flex-1 truncate text-[#2C2A29]">{f.filename}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => removeFile(f.id)}
                                                    disabled={submitting}
                                                    className="text-[#78736E] hover:text-[#C2410C] shrink-0"
                                                    data-testid={`montor-report-remove-file-${f.id}`}
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider text-[#78736E]">Send til leverandør</Label>
                            <Select
                                value={recipientKey}
                                onValueChange={setRecipientKey}
                                disabled={submitting || loadingRecipients}
                            >
                                <SelectTrigger className="mt-1.5" data-testid="montor-report-recipient">
                                    <SelectValue placeholder={loadingRecipients ? "Henter..." : "Vælg leverandør"} />
                                </SelectTrigger>
                                <SelectContent>
                                    {recipients.suppliers.map((s) => (
                                        <SelectItem key={`s-${s.id}`} value={`supplier:${s.id}`} data-testid={`montor-report-recipient-supplier-${s.id}`}>
                                            {s.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-[#A19E9A] mt-1.5">Reklamationen sendes automatisk til leverandøren med det samme.</p>
                        </div>

                        <Button
                            type="submit"
                            disabled={submitting || uploading}
                            className="w-full bg-[#4A4641] hover:bg-[#2C2A29] text-white"
                            data-testid="montor-report-submit"
                        >
                            {submitting ? (
                                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Sender...</>
                            ) : (
                                "Indsend reklamation"
                            )}
                        </Button>
                    </form>
                </div>

                <div className="mt-6 text-center text-xs text-[#78736E]">
                    Oprettes som ny sag i kundeservice-dashboardet.
                </div>
            </div>
        </div>
    );
}
